import { useEffect, useMemo } from "react";
import { BaseFormControlledModal } from "../../../../../../common/components/base_form_controlled_modal";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Stack } from "@mui/material";
import { ObraChecklistServicoLiberacao } from "../../../../domain/models/obra_checklist_servico_liberacao";
import { defaultObraChecklistServicoLiberacaoForm, ObraChecklistServicoLiberacaoForm, ObraChecklistServicoLiberacaoItemForm, ObraChecklistServicoLiberacaoSchema } from "../../../schemas/Obras_checklistarea/obra_checklist_servico_liberacao";
import { LegendComponent } from "../../../../../../common/components/legend_component";
import { LegendTagIcon } from "../../../../../../common/components/legend_tag_icon";
import { LegendTagItemComponent } from "../../../../../../common/components/legend_tag_item_component";
import { GenerateObraChecklistServicoLiberacaoColumnsDefinition } from "../columns/obra_checklist_servico_liberacao_itens";
import { StyledDataGrid } from "../../../../../../common/components/styled_data_grid";
import { ObraHeaderInfo } from "../../../../domain/models/obra_header_info";
import { DefaultFormSelectInput } from "../../../../../../common/components/default_form_select_input";
import { DefaultFormDatePickerInput } from "../../../../../../common/components/default_form_date_picker_input";
import { DefaultFormAutocomplete } from "../../../../../../common/components/default_form_autocomplete";
import { FuncionarioDropDown } from "../../../../../../common/domain/models/funcionario_dropdown";

import FormContainer from "../../../../../../common/components/form_container";
import DefaultInput from "../../../../../../common/components/default_input";
import { parseDataLiberacao } from "../utils/data_liberacao";

/**
 * ObraChecklistServicoLiberarModal
 */

type Props = {
  open: boolean;
  saving: boolean;
  loading: boolean;
  readOnly?: boolean;
  liberacao: ObraChecklistServicoLiberacao | null;
  selectedObra: ObraHeaderInfo | null;
  areaDescricao: string | null;
  checklistDescricao: string | null;
  funcionariosDropdown: FuncionarioDropDown[];
  onClose: () => void;
  onSubmit: (form: ObraChecklistServicoLiberacaoForm) => void;
};

export const ObraChecklistServicoLiberacaoModal = ({
  open,
  saving,
  loading,
  readOnly = false,
  liberacao,
  selectedObra,
  areaDescricao,
  checklistDescricao,
  funcionariosDropdown,
  onClose,
  onSubmit,
}: Props) => {
  const hookForm = useForm<ObraChecklistServicoLiberacaoForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: { ...defaultObraChecklistServicoLiberacaoForm },
    resolver: zodResolver(ObraChecklistServicoLiberacaoSchema),
  });

  const statusOptions = useMemo(() => {
    return [
      {
        id: 0,
        label: 'Não',
      },
      {
        id: 1,
        label: 'Sim',
      },
      {
        id: 2,
        label: 'Não se aplica',
      },
    ];
  }, [
  ]);

  const itens = hookForm.watch('itens');

  const columnsDef = useMemo(() => {
    return GenerateObraChecklistServicoLiberacaoColumnsDefinition({
      onActionInspecao1Change: (idChecklistServicoLiberacao: number, value: string | number | null) => {
        if (readOnly || loading || saving) return;

        hookForm.setValue('itens', hookForm.getValues('itens').map(item =>
          item.idChecklistServicoLiberacao === idChecklistServicoLiberacao
            ? {
              ...item,
              inspecao1: value?.toString() ?? null,
              inspecao2: null,
            }
            : item
        ), { shouldDirty: true });
      },
      onActionInspecao2Change: (idChecklistServicoLiberacao: number, value: string | number | null) => {
        if (readOnly || loading || saving) return;

        hookForm.setValue('itens', hookForm.getValues('itens').map(item =>
          item.idChecklistServicoLiberacao === idChecklistServicoLiberacao && item.inspecao1 === 'R'
            ? { ...item, inspecao2: value?.toString() ?? null }
            : item
        ), { shouldDirty: true });
      },
      onActionLimpar: (idChecklistServicoLiberacao: number) => {
        if (readOnly || loading || saving) return;

        hookForm.setValue('itens', hookForm.getValues('itens').map(item =>
          item.idChecklistServicoLiberacao === idChecklistServicoLiberacao
            ? {
              ...item,
              inspecao1: null,
              inspecao2: null,
            }
            : item
        ), { shouldDirty: true });
      },
    });
  }, [
    hookForm,
    readOnly,
    loading,
    saving,
  ]);

  useEffect(() => {
    const itens = liberacao?.itens ?? [];
    hookForm.reset({
      ...defaultObraChecklistServicoLiberacaoForm,
      status: liberacao?.status ?? 0,
      dataLiberacao: parseDataLiberacao(liberacao?.dataLiberacao),
      idFuncionarioResponsavel: liberacao?.idFuncionarioResponsavel ?? null,
      itens: itens.map(item => ({
        idChecklistServicoLiberacao: item.idChecklistServicoLiberacao,
        descricao: item.descricao,
        inspecao1: item.inspecao1,
        inspecao2: item.inspecao2,
      })) ?? [],
    });
  }, [
    open,
    liberacao,
    hookForm,
  ]);

  return (
    <BaseFormControlledModal
      open={open}
      saving={saving}
      loading={loading}
      readOnly={readOnly || !liberacao?.itens.length}
      title='Liberação do checklist de serviço'
      width="100%"
      onClose={onClose}
      onSubmit={hookForm.handleSubmit(onSubmit)}>
      <div className="w-full flex flex-col gap-2">
        <FormContainer
          title={"Informações"}
          arrangement="fullRow"
          gap="small"
          padding="small"
          border={false}>
          <>
            <div className="w-full grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-2">
              <DefaultInput
                name="centroCusto"
                label="Centro de Custo"
                value={selectedObra?.centroCusto}
                readOnly={true}
              />

              <DefaultInput
                name="area"
                label={"Área"}
                value={areaDescricao}
                readOnly={true}
              />

              <DefaultInput
                name="checklist"
                label={"Checklist de Serviço"}
                value={checklistDescricao}
                readOnly={true}
              />
            </div>
          </>
        </FormContainer>

        <FormContainer
          title=""
          arrangement="fullRow"
          gap="small"
          padding="small"
          border={false}>
          <>
            <div className="w-full grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-2">
              <DefaultFormSelectInput
                name='status'
                label='Status'
                readOnly={readOnly || loading || saving}
                options={statusOptions}
                showSelectionLabel={false}
                reactHookForm={hookForm}
              />

              <DefaultFormDatePickerInput
                name='dataLiberacao'
                label='Data da liberação'
                readOnly={readOnly || loading || saving}
                reactHookForm={hookForm}
              />

              <DefaultFormAutocomplete
                name='idFuncionarioResponsavel'
                label='Responsável'
                readOnly={readOnly || loading || saving}
                getOptionKey={x => x.idFuncionario}
                getOptionLabel={x => x.nome}
                getOptionDisabled={x => !x.canBeSelected}
                options={funcionariosDropdown}
                reactHookForm={hookForm}
              />
            </div>
          </>
        </FormContainer>

        <Stack spacing={0}>
          <div className="w-full flex flex-row justify-between items-end pt-2">
            <div className="flex flex-col flex-wrap">
              <LegendComponent>
                <LegendTagItemComponent
                  icon={<LegendTagIcon title='NA' bgColor="#89c4f4" />}
                  label='Não Se Aplica'
                />

                <LegendTagItemComponent
                  icon={<LegendTagIcon title='L' bgColor="#45b6af" />}
                  label='Liberado'
                />

                <LegendTagItemComponent
                  icon={<LegendTagIcon title='R' bgColor="#f8cb00" />}
                  label='Reprovado'
                />

                <LegendTagItemComponent
                  icon={<LegendTagIcon title='LNI' bgColor="#45b6af" />}
                  label='Liberado após nova inspeção'
                />

              </LegendComponent>
            </div>
          </div>

          <StyledDataGrid
            tableName="obra_itens_checklist_area_verificacao_inspecoes_items"
            disableColumnMenu={false}
            getRowId={(row: ObraChecklistServicoLiberacaoItemForm) => row.idChecklistServicoLiberacao}
            getRowHeight={() => 'auto'}
            height="25rem"
            textSize="muitoPequeno"
            columns={columnsDef}
            loading={loading}
            rows={itens ?? []}
            disableRowSelectionOnClick={true}
            disableColumnSorting={false}
          />

        </Stack>
      </div>
    </BaseFormControlledModal>
  );
}
